import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer } from '@/lib/api/baseQuery';
import { StockSearchResult } from '@/types/stock-scanner/screener';
import { createBaseQuery } from '@/lib/api/baseConfig';

export interface FiiDiiValues {
  buyValue: number;
  sellValue: number;
  netValue: number;
}

export interface FiiDiiEntry {
  _id: string;
  date: string;
  DII: FiiDiiValues;
  'FII/FPI': FiiDiiValues;
  source: string;
  updatedAt: string;
}

export interface FiiDiiResponse {
  statusCode: number;
  data: FiiDiiEntry[];
}

interface StockScannerDetailsApiResponse {
  data?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

interface StockScannerTechnicalApiResponse {
  statusCode?: number;
  data?: {
    symbol?: string;
    raw?: Record<string, unknown>;
    nsename?: string;
    bsename?: string;
  };
  message?: string;
  status?: boolean;
}

interface StockScreenerListApiResponse {
  data?: Array<StockSearchResult | Record<string, unknown>>;
  result?: Array<StockSearchResult | Record<string, unknown>>;
  records?: Array<StockSearchResult | Record<string, unknown>>;
}

export interface StockChartPoint {
  date: string;
  closing_price: number;
}

export interface StockChartApiResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  data?: {
    id?: string;
    exchange?: string;
    period?: string;
    date_range?: {
      from?: string;
      to?: string;
    };
    total_records?: number;
    data?: StockChartPoint[];
  };
}

const normalizeSearchItem = (
  item: StockSearchResult | Record<string, unknown>,
): StockSearchResult | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  // Handle both old format (symbol, company_name) and new format (nse_symbol, bse_symbol, company_name)
  let symbol = String((item as any).symbol ?? '').trim();
  const nseSymbol = String((item as any).nseSymbol ?? (item as any).nse_symbol ?? '').trim();
  const bseSymbol = String((item as any).bseSymbol ?? (item as any).bse_symbol ?? '').trim();
  const isin = String((item as any).isin ?? '').trim();

  if (!symbol) {
    // Prefer NSE symbol, fall back to BSE
    symbol = nseSymbol || bseSymbol;
  }

  const companyName = String((item as any).companyName ?? (item as any).company_name ?? '').trim();

  // Determine exchange based on available symbol
  let exchange = String((item as any).exchange ?? 'NSE').trim();
  if (!exchange || exchange === 'NSE') {
    exchange = nseSymbol ? 'NSE' : bseSymbol ? 'BSE' : 'NSE';
  }

  if (!symbol || !companyName) {
    return null;
  }

  return {
    symbol,
    companyName,
    exchange,
    nseSymbol: nseSymbol || undefined,
    bseSymbol: bseSymbol || undefined,
    isin: isin || undefined,
    sector: typeof (item as any).sector === 'string' ? (item as any).sector : undefined,
  };
};

const normalizeSearchList = (
  items: Array<StockSearchResult | Record<string, unknown>>,
): StockSearchResult[] => {
  return items.map(normalizeSearchItem).filter((item): item is StockSearchResult => item !== null);
};

export const marketInsightApi = createApi({
  reducerPath: 'marketInsightApi',
  baseQuery: createBaseQuery('stockscanner'),
  tagTypes: [
    'FiiDii',
    'Corporate Action',
    'Announcements',
    'Concalls',
    'Stock Calendar',
    'Trade Deals',
    'Insider Trades',
    'Stock Search',
    'Stock Scanner Details',
  ],
  endpoints: (builder) => ({
    getFiiDii: builder.query<FiiDiiResponse, { frequency?: string } | void>({
      query: (arg) => ({
        url: '/v2/marketinsight/fii-dii',
        params: { source: 'ap', ...(arg?.frequency ? { frequency: arg.frequency } : {}) },
      }),
      transformErrorResponse: createApiErrorTransformer('FiiDii', false),
    }),

    getCorporateAction: builder.query<
      any,
      { page?: number; category?: string; limit?: number; company_name?: string } | void
    >({
      query: (arg) => ({
        url: '/v2/marketinsight/corporate-actions',
        params: {
          source: 'ap',
          ...(arg?.page ? { page: arg.page } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
          ...(arg?.category ? { category: arg.category } : {}),
          ...(arg?.company_name ? { search: arg.company_name } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('Corporate Action', false),
    }),

    getAnnouncements: builder.query<
      any,
      { page?: number; limit?: number; source?: string; company_name?: string } | void
    >({
      query: (arg) => ({
        url: '/v2/marketinsight/announcements',
        params: {
          source: arg?.source ?? 'ap',
          ...(arg?.page ? { page: arg.page } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
          ...(arg?.company_name ? { search: arg.company_name } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('Announcements', false),
    }),

    getConcalls: builder.query<
      any,
      { page?: number; limit?: number; source?: string; company_name?: string } | void
    >({
      query: (arg) => ({
        url: '/v2/marketinsight/concalls',
        params: {
          source: arg?.source ?? 'ap',
          ...(arg?.page ? { page: arg.page } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
          ...(arg?.company_name ? { search: arg.company_name } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('Concalls', false),
    }),

    getStockCalendar: builder.query<
      any,
      { page?: number; limit?: number; source?: string; company_name?: string } | void
    >({
      query: (arg) => ({
        url: '/v2/marketinsight/stock-calendar',
        params: {
          source: arg?.source ?? 'ap',
          ...(arg?.page ? { page: arg.page } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
          ...(arg?.company_name ? { search: arg.company_name } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('Stock Calendar', false),
    }),

    getTradeDeals: builder.query<
      any,
      {
        page?: number;
        limit?: number;
        category?: string;
        source?: string;
        sort?: string;
        company_name?: string;
      } | void
    >({
      query: (arg) => ({
        url: '/v2/marketinsight/trade-deals',
        params: {
          source: arg?.source ?? 'ap',
          ...(arg?.company_name ? { search: arg.company_name } : {}),
          ...(arg?.category ? { category: arg.category } : {}),
          ...(arg?.sort ? { sort: arg.sort } : {}),
          ...(arg?.page ? { page: arg.page } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('Trade Deals', false),
    }),

    getInsiderTrades: builder.query<
      any,
      { page?: number; limit?: number; source?: string; company_name?: string } | void
    >({
      query: (arg) => ({
        url: '/v2/marketinsight/trade-deals/insider',
        params: {
          source: arg?.source ?? 'ap',
          ...(arg?.company_name ? { search: arg.company_name } : {}),
          ...(arg?.page ? { page: arg.page } : {}),
          ...(arg?.limit ? { limit: arg.limit } : {}),
        },
      }),
      transformErrorResponse: createApiErrorTransformer('Insider Trades', false),
    }),

    searchStocks: builder.query<StockSearchResult[], string>({
      query: (search) => ({
        url: '/v2/stock-screener/list',
        params: {
          search,
          source: 'ap',
        },
      }),
      transformResponse: (response: StockScreenerListApiResponse | StockSearchResult[]) => {
        if (Array.isArray(response)) {
          return normalizeSearchList(response);
        }

        if (Array.isArray(response?.data)) {
          return normalizeSearchList(response.data);
        }

        if (Array.isArray(response?.result)) {
          return normalizeSearchList(response.result);
        }

        if (Array.isArray(response?.records)) {
          return normalizeSearchList(response.records);
        }

        return [];
      },
      transformErrorResponse: createApiErrorTransformer('Stock Search', false),
    }),

    getStockScannerDetails: builder.mutation<Record<string, unknown>, { symbol: string }>({
      query: ({ symbol }) => ({
        url: '/v2/stock-screener/details',
        method: 'POST',
        params: {
          source: 'ap',
        },
        body: {
          symbol,
        },
      }),
      transformResponse: (response: StockScannerDetailsApiResponse | Record<string, unknown>) => {
        if (response && typeof response === 'object' && 'data' in response && response.data) {
          return response.data as Record<string, unknown>;
        }

        if (response && typeof response === 'object' && 'result' in response && response.result) {
          return response.result as Record<string, unknown>;
        }

        return (response ?? {}) as Record<string, unknown>;
      },
      transformErrorResponse: createApiErrorTransformer('Stock Scanner Details', false),
    }),

    getStockScannerTechnical: builder.query<
      Record<string, unknown>,
      { symbol: string; exchange?: string; period?: string }
    >({
      query: ({ symbol, exchange = 'NSE', period }) => ({
        url: '/v2/stock-screener/technical',
        params: {
          symbol,
          exchange,
          source: 'ap',
          ...(period ? { period } : {}),
        },
      }),
      transformResponse: (response: StockScannerTechnicalApiResponse | Record<string, unknown>) => {
        if (response && typeof response === 'object' && 'data' in response && response.data) {
          const payload = response.data as { raw?: Record<string, unknown> };
          if (payload.raw && typeof payload.raw === 'object') {
            return payload.raw;
          }
        }

        return (response ?? {}) as Record<string, unknown>;
      },
      transformErrorResponse: createApiErrorTransformer('Stock Scanner Technical', false),
    }),

    getStockChartData: builder.query<
      StockChartApiResponse,
      { id: string; type: 'nse' | 'bse'; period: string }
    >({
      queryFn: async ({ id, type, period }) => {
        try {
          const baseUrl = import.meta.env.VITE_STOCK_CHART_URL?.trim();
          if (!baseUrl) {
            return {
              error: {
                status: 'CUSTOM_ERROR' as const,
                error: 'Stock chart base URL is not configured.',
              },
            };
          }

          const url = new URL('api/v2/scheme/get-stock-data', `${baseUrl.replace(/\/$/, '')}/`);
          url.searchParams.set('id', id);
          url.searchParams.set('type', type);
          url.searchParams.set('period', period);

          const response = await fetch(url.toString(), { method: 'GET' });
          const json = (await response.json()) as StockChartApiResponse;

          if (!response.ok || json?.success === false) {
            return {
              error: {
                status: response.status || 500,
                data: json,
              },
            };
          }

          return { data: json };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: error instanceof Error ? error.message : 'Unable to load stock chart data',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetFiiDiiQuery,
  useGetCorporateActionQuery,
  useGetAnnouncementsQuery,
  useGetConcallsQuery,
  useGetStockCalendarQuery,
  useGetTradeDealsQuery,
  useGetInsiderTradesQuery,
  useLazySearchStocksQuery,
  useGetStockScannerDetailsMutation,
  useGetStockScannerTechnicalQuery,
  useGetStockChartDataQuery,
  useLazyGetStockChartDataQuery,
} = marketInsightApi;
